import { Calendar16 } from '@carbon/icons-react';
import { keyBy } from 'lodash';
import moment from 'moment';
import { pipe } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DateType, PermissionSetType } from 'api/models';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { CustomDueDatePicker } from 'components/atoms/due-date-picker';
import { Input, TextArea } from 'components/atoms/input';
import {
  durationParser,
  formatter,
  getError,
  getMinutes,
  nonEmpty,
  useInputDuration,
  withGreaterThanZero,
  withZeroAsError,
} from 'components/atoms/input-duration';
import { Select } from 'components/atoms/select';
import { Color } from 'components/colors';
import { TimeoffDate } from 'components/organisms/module-settings/profile/timeoff-holiday-calendar/timeoff-date';
import { withLessThanOneDayParser } from 'components/organisms/timesheets/common/utils';
import {
  RoleRenderer,
  getRoleTeamOptionsForTimeEntry,
} from 'components/organisms/timesheets/timesheet-role';
import {
  asList,
  useCategories,
} from 'components/pages/timesheet-settings/categories';
import {
  filter,
  isEmpty,
  map,
  Maybe,
  ofNullable,
  orElse,
  orUndefined,
} from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { BodyShort01 } from 'ozone/atoms';
import {
  useAllTeamsInAccount,
  usePlaceholderResolutionsQuery,
} from 'queries/placeholder';
import { useVendorRolesQuery } from 'queries/roles';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { getRandomColor } from 'utils/get-random-color';
import {
  ButtonGroup,
  Categories,
  Duration,
  Form,
  Notes,
  Row,
} from './components';
import { DrawerContent } from './drawer-content';
import { useTimeSpent } from './use-time-spent';
import { viewTimesheetButton } from './view-timesheet-button';

const StyledDatePicker = styled(Form.Item)`
  .date-picker-trigger {
    display: flex;
    width: 325px;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-04) var(--spacing-05);
    background-color: var(--gray-10);
    resize: none;
    font-size: var(--font-size-base);
    line-height: var(--line-height-body-short-01);
    outline: var(--spacing-01) solid;
    outline-color: transparent;
    outline-offset: calc(var(--spacing-01) * -1);
    border: none;
    border-bottom: 1px solid var(--gray-50);
    border-radius: 0;
  }
  .calendar_date_text {
    span:last-child {
      color: var(--gray-50);
    }
  }
`;

type Option = {
  label: string;
  value: number;
  color: Color;
};

interface Props {
  entryDate: DateType;
  onDateChange;
  minutes: Maybe<number>;
  onMinutesChange: (next: Maybe<number>) => void;
  billable: boolean;
  billableDisabled: boolean;
  onBillableChange: (next: boolean) => void;
  category: Maybe<number>;
  onCategoryChange: (next: Maybe<number>) => void;
  notes: Maybe<string>;
  onNotesChange: (next: Maybe<string>) => void;
  roleId: Maybe<string>;
  setRoleId: (next: Maybe<string>) => void;
  onSave: () => void;
  onCancel: () => void;
  timeSpent: ReturnType<typeof useTimeSpent>['timeSpent'];
  isAllowedDueToAllocation: boolean;
  projectId: number;
}

const RoleWrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
  margin-bottom: var(--spacing-03);
`;

export const AddTimeEntryDrawerControlled: FC<Props> = ({
  entryDate,
  onDateChange,
  minutes,
  onMinutesChange,
  billable,
  billableDisabled,
  onBillableChange,
  category: categoryId,
  onCategoryChange: setCategoryId,
  notes,
  onNotesChange,
  onSave,
  onCancel,
  timeSpent,
  isAllowedDueToAllocation,
  roleId: roleIdState,
  setRoleId,
  projectId,
}) => {
  const parser = pipe(
    () => durationParser,
    withGreaterThanZero,
    withZeroAsError,
    withLessThanOneDayParser,
    nonEmpty,
  )();
  const { data: configuration } = useTimesheetConfiguration();
  const { configMeta } = configuration || {};
  const { categoryMandatory } = configMeta || {};
  const userPermissionType = useUserPermissionSetType();
  const isUserAdmin = userPermissionType === PermissionSetType.AccountAdmin;

  const { categories } = useCategories();
  const { handleBlur, handleChange, handlePressEnter, value, state } =
    useInputDuration(minutes, parser);
  const duration = useMemo(() => getMinutes(state), [state]);
  const error = useMemo(() => getError(state), [state]);

  const { user } = useUser();

  const { data: placeholderResolutions } = usePlaceholderResolutionsQuery();
  const { data: roles, isLoading: isRolesLoading } = useVendorRolesQuery();
  const { data: teams, isLoading: isTeamLoading } = useAllTeamsInAccount();

  const roleLookup = useMemo(() => {
    if (isRolesLoading) {
      return undefined;
    }

    return keyBy(roles, 'roleId');
  }, [isRolesLoading, roles]);

  const teamLookup = useMemo(() => {
    if (isTeamLoading) {
      return undefined;
    }

    return keyBy(teams, 'teamId');
  }, [isTeamLoading, teams]);

  const { options: roleOptions } = getRoleTeamOptionsForTimeEntry(
    user?.userId,
    projectId,
    placeholderResolutions,
    roleLookup,
    teamLookup,
    user.role?.roleId,
  );

  useEffect(() => onMinutesChange(duration), [duration, onMinutesChange]);
  const translate = useTranslate();
  const categoryOptions: Option[] = useMemo(
    () =>
      asList(categories).map(([, { id, name }]) => ({
        value: id,
        label: name,
        color: getRandomColor(id),
      })),
    [categories],
  );
  const category = useMemo(
    () =>
      ofNullable(
        categoryOptions.find((x) => x.value === categoryId[1]) ?? null,
      ),
    [categoryId, categoryOptions],
  );
  const selectCategory = useCallback(
    (option?: Option) =>
      pipe(
        () => option ?? null,
        ofNullable,
        map((x) => x.value),
        setCategoryId,
      )(),
    [setCategoryId],
  );
  const selectRoleId = useCallback(
    (id?: string) =>
      pipe(
        () => id ?? null,
        ofNullable,
        map((x) => x),
        setRoleId,
      )(),
    [setRoleId],
  );
  const saveCaption = useTranslation('task.add_time_entry.save');
  const primary = useMemo(
    () => ({
      title: saveCaption,
      onClick: onSave,
      disabled:
        !isAllowedDueToAllocation ||
        (categoryMandatory
          ? isEmpty(minutes) || isEmpty(categoryId)
          : isEmpty(minutes)),
    }),
    [
      saveCaption,
      onSave,
      categoryMandatory,
      minutes,
      categoryId,
      isAllowedDueToAllocation,
    ],
  );
  const cancelCaption = useTranslation('task.add_time_entry.cancel');
  const secondary = useMemo(
    () => ({
      title: cancelCaption,
      onClick: onCancel,
    }),
    [cancelCaption, onCancel],
  );
  const formattedTimeSpent = useMemo(
    () => pipe(() => timeSpent, maybeValueOf, map(formatter), orElse('...'))(),
    [timeSpent],
  );
  const validateStatus = useMemo(
    () =>
      pipe(
        () => error,
        filter((e) => e !== 'empty'),
        map(() => 'error'),
        orUndefined,
      )(),
    [error],
  );

  const today = moment().format('YYYY-MM-DD');

  const handleDueDateChange = ([startDate, endDate]) => {
    let dateVal = today;

    if (startDate === null && endDate === null) {
      dateVal = today;
    } else if (startDate !== null) {
      dateVal = startDate;
    } else if (endDate !== null) {
      dateVal = endDate;
    }

    onDateChange(dateVal);
  };

  return (
    <DrawerContent>
      <DrawerContent.Title data-cy="task.add_time_entry.title">
        {useTranslation('task.add_time_entry')}
      </DrawerContent.Title>
      <DrawerContent.Content>
        <Row>
          <BodyShort01 data-cy="task.add_time_entry.total_time_spent">
            {useTranslation('task.add_time_entry.total_time_spent')}:{' '}
            <strong>{formattedTimeSpent}</strong>
          </BodyShort01>
        </Row>
        <Form layout="inline" initialValues={{ entryDate: today }}>
          <Row />
          <Form.Item
            name="entryDate"
            label={useTranslation('task.add_time_entry.date')}
            extra={
              !isAllowedDueToAllocation
                ? translate(
                    'settings.timesheets.controls.entry_type.dates.time.not_allocated',
                  )
                : undefined
            }
            rules={[
              {
                required: true,
              },
            ]}
          >
            <StyledDatePicker>
              <CustomDueDatePicker
                onDateChange={handleDueDateChange}
                renderTrigger={(togglePickerVisibility) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <li
                    role="menuitem"
                    onClick={() => {
                      togglePickerVisibility();
                    }}
                    className="date-picker-trigger"
                  >
                    <TimeoffDate startDate={entryDate} dueDate={entryDate} />
                    <Calendar16 />
                  </li>
                )}
                disabledDate={(currentDate) => {
                  if (
                    configuration?.lockSetting.freeze.enabled &&
                    !isUserAdmin
                  ) {
                    if (
                      currentDate.isSameOrBefore(
                        moment(configuration.lockSetting.freeze.enableAfter),
                        'day',
                      )
                    ) {
                      return true;
                    }
                  }

                  return false;
                }}
              />
            </StyledDatePicker>
          </Form.Item>
        </Form>
        <Form>
          <Duration
            required
            validateStatus={validateStatus}
            label={useTranslation('task.add_time_entry.duration')}
          >
            <Input
              placeholder={useTranslation(
                'timesheets.placeholders.time_tracked',
              )}
              id="task_pane_time_entry_duration"
              value={value}
              onChange={handleChange}
              onPressEnter={handlePressEnter}
              onBlur={handleBlur}
            />
          </Duration>
          <Form.Item label={useTranslation('task.add_time_entry.billable')}>
            <Checkbox
              data-cy="task.add_time_entry.billable"
              checked={billable}
              disabled={billableDisabled}
              onChange={(e) => onBillableChange(e.target.checked)}
            />
          </Form.Item>
        </Form>
        {roleOptions.length > 0 && (
          <RoleWrapper>
            <FormattedMessage id="timesheets.column.role" />
            <RoleRenderer
              roleOptions={roleOptions}
              defaultRoleId={orUndefined(roleIdState) ?? `${user.role?.roleId}`}
              updateTimeEntry={(id) => {
                selectRoleId(id);
              }}
            />
          </RoleWrapper>
        )}

        <Form layout="inline">
          <Categories
            label={useTranslation('task.add_time_entry.category')}
            required={categoryMandatory}
          >
            <Select
              type="tag"
              options={categoryOptions}
              value={orUndefined(category)}
              onChange={selectCategory}
              isClearable
            />
          </Categories>
        </Form>
        <Form layout="inline">
          <Notes label={useTranslation('task.add_time_entry.notes')}>
            <TextArea
              value={orUndefined(notes)}
              placeholder={useTranslation('timesheets.placeholders.notes')}
              onChange={(e) =>
                onNotesChange(ofNullable(e.target.value ?? null))
              }
            />
          </Notes>
        </Form>
        <Row data-cy="task.add_time_entry.view_timesheet">
          {viewTimesheetButton(translate, entryDate)}
        </Row>
      </DrawerContent.Content>
      <DrawerContent.Footer>
        <ButtonGroup $reversed>
          <Button
            data-cy="task.add_time_entry.save"
            type="primary"
            onClick={primary.onClick}
            disabled={primary.disabled}
          >
            {primary.title}
          </Button>
          <Button
            data-cy="task.add_time_entry.cancel"
            onClick={secondary.onClick}
          >
            {secondary.title}
          </Button>
        </ButtonGroup>
      </DrawerContent.Footer>
    </DrawerContent>
  );
};
